import { CustomTable, tableButton } from 'components/CustomTable';
import { PagesFilters } from 'components/Filters/PagesFilters';
import { TableSwitchCell } from 'components/TableSwitchCell';
import Loader from 'components/Loader';
import { showToast } from 'components/Toast';
import { has_permission } from 'config/permissions';
import { ITableColumn, recipientColumns } from 'constants/tables';
import { useAppSelector } from 'helpers/hooks';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getActiveRole } from 'selectors/admins';
import {
  useGetAdminRecipientsQuery,
  useUpdateRecipientOnboardingCompleteMutation,
  useUpdateRecipientStripeAuthorizedMutation,
} from 'services/adminRecipientsApi';
import { trackEvent } from 'helpers/mixpanelEvents';

interface IData {
  id: string;
  recipient_id?: string;
  name?: string;
  status?: string;
  is_stripe_authorized?: boolean;
  onboarding_form_complete?: boolean;
  actions?: ReactElement;
}

export const RecipientsList = () => {
  const navigate = useNavigate();
  const [dataState, setDataState] = useState<IData[]>([]);
  const [filterSelected, setFilterSelected] = useState('all');
  const activeRole = useAppSelector(getActiveRole);

  const { data: recipientsData = [], isLoading } = useGetAdminRecipientsQuery(undefined, {
    skip: !activeRole,
  });

  const [updateStripeAuthorized] = useUpdateRecipientStripeAuthorizedMutation();
  const [updateOnboardingComplete] = useUpdateRecipientOnboardingCompleteMutation();

  const createRecipient = () => {
    navigate('/recipients_detail');
  };

  const handleUpdate = useCallback(
    (_type: string, id?: string) => {
      if (id) {
        navigate(`/recipients_detail?recipient_id=${id}`);
      }
    },
    [navigate]
  );

  const handleConfirmStripeAuthorized = useCallback(
    async (recipientId: string, isStripeAuthorized: boolean) => {
      try {
        await updateStripeAuthorized({
          recipientId,
          isStripeAuthorized,
        }).unwrap();
        trackEvent('Recipient Status Toggled', {
          screen: 'recipients',
          field: 'is_stripe_authorized',
          enabled: isStripeAuthorized,
        });
        showToast('Stripe authorized status updated successfully.', 'success');
      } catch (error: any) {
        showToast(
          error?.data?.message || error?.message || 'An error occurred',
          'error',
          error?.status
        );
      }
    },
    [updateStripeAuthorized]
  );

  const handleConfirmOnboardingComplete = useCallback(
    async (recipientId: string, onboardingFormComplete: boolean) => {
      try {
        await updateOnboardingComplete({
          recipientId,
          onboardingFormComplete,
        }).unwrap();
        trackEvent('Recipient Status Toggled', {
          screen: 'recipients',
          field: 'onboarding_form_complete',
          enabled: onboardingFormComplete,
        });
        showToast('Onboarding form complete status updated successfully.', 'success');
      } catch (error: any) {
        showToast(
          error?.data?.message || error?.message || 'An error occurred',
          'error',
          error?.status
        );
      }
    },
    [updateOnboardingComplete]
  );

  const columnsWithSwitch = useMemo(() => {
    return recipientColumns.map((col: ITableColumn) => {
      if (col.accessor === 'is_stripe_authorized') {
        return {
          ...col,
          renderCell: (item: IData) => (
            <TableSwitchCell
              value={Boolean(item.is_stripe_authorized)}
              onText="YES"
              offText="NO"
              saveLabel="Save stripe authorized"
              onSave={value =>
                item.recipient_id
                  ? handleConfirmStripeAuthorized(item.recipient_id, value)
                  : Promise.resolve()
              }
            />
          ),
        };
      }
      if (col.accessor === 'onboarding_form_complete') {
        return {
          ...col,
          renderCell: (item: IData) => (
            <TableSwitchCell
              value={Boolean(item.onboarding_form_complete)}
              onText="YES"
              offText="NO"
              saveLabel="Save onboarding form complete"
              onSave={value =>
                item.recipient_id
                  ? handleConfirmOnboardingComplete(item.recipient_id, value)
                  : Promise.resolve()
              }
            />
          ),
        };
      }
      return col;
    });
  }, [handleConfirmStripeAuthorized, handleConfirmOnboardingComplete]);

  useEffect(() => {
    if (!isLoading && recipientsData?.length > 0)
      setDataState(
        recipientsData
          .filter(rec =>
            filterSelected === 'all' ? rec.de_status : rec.de_status === filterSelected
          )
          .map(prop => {
            return {
              // Keyed by recipient: each switch cell holds its own unsaved value, which must
              // not move to another recipient when the tab filter reorders the rows.
              id: prop.recipient_id,
              recipient_id: prop.recipient_id,
              name: prop.recipient_name,
              // Year comes off the raw ISO string: parsing it to a Date renders in local
              // time, which drops a Jan 1 election into the prior year west of UTC.
              subtype:
                prop.recipient_type === 'CANDIDATE'
                  ? [
                      prop.race?.election.display_info,
                      prop.race?.election.election_date?.slice(0, 4),
                    ]
                      .filter(Boolean)
                      .join(' ')
                  : prop.recipient_subtype,
              de_status: prop.de_status,
              de_id: prop.de_id,
              stripe_status: prop.stripe_status,
              stripe_id: prop.stripe_id || 'N/A',
              is_stripe_authorized: Boolean(prop.is_stripe_authorized),
              onboarding_form_complete: Boolean(prop.onboarding_form_complete),
              actions: (
                <div className="grid-column">
                  <div className="table-action-row">
                    {has_permission('RECIPIENTS_ROUTE', 'U', activeRole) &&
                      tableButton(handleUpdate, 'edit', prop.recipient_id)}
                  </div>
                </div>
              ),
            };
          })
      );
  }, [recipientsData, filterSelected, isLoading]);

  return (
    <>
      {isLoading && <Loader type="dots" />}

      {!has_permission('RECIPIENTS_ROUTE', 'R', activeRole) ? (
        <div className="content-full-width">
          <h1 className="title-permission">
            Sorry, you don&apos;t have permission to see this screen.
          </h1>
        </div>
      ) : (
        <>
          <div className="row filters-row">
            <PagesFilters setFilterSelected={setFilterSelected} allValue="Recipients" />
          </div>
          <div className="content-full-width">
            <div className="row custom-header-container">
              <div className="grid-column col-lg-9 col-md-8 col-sm-12 align-center">
                <span className="oath-h3 inkBlue">Oath Recipients</span>
                <span className="oath-bodysmall inkBlue">{dataState?.length} Total</span>
              </div>
              <div className="new-page-button col-lg-3 col-md-4 col-sm-12">
                <button
                  type="button"
                  className="btn-inkBlue btn btn-secondary btn-block"
                  onClick={createRecipient}
                >
                  Create New Recipient
                </button>
              </div>
            </div>
            <div className="row recent-donations-table">
              <div className="col-lg-12">
                <CustomTable
                  key="recipient-list"
                  title="Recipients"
                  data={dataState}
                  columns={columnsWithSwitch}
                  withPagination
                  screen="recipients"
                />
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};
